/**
 * Trainova AI — English (Canonical Locale Dictionary)
 */

export const en: Record<string, string> = {
  // Navigation & Header
  'nav.brand': 'Trainova AI',
  'nav.dashboard': 'Dashboard',
  'nav.simulations': 'Simulations',
  'nav.scenarios': 'Scenarios',
  'nav.courses': 'Courses',
  'nav.my_attempts': 'My Attempts',
  'nav.assessments': 'Assessments',
  'nav.reports': 'Reports',
  'nav.analytics': 'Analytics',
  'nav.settings': 'Settings',
  'nav.admin_console': 'Administration Console',
  'nav.tenant_settings': 'Tenant Settings',
  'nav.sign_out': 'Sign Out',
  'nav.profile': 'Profile',
  'nav.language': 'Language',
  'nav.menu': 'Menu',
  'nav.close': 'Close',
  'nav.home': 'Home',
  'nav.my_results': 'My Results',
  'nav.learners': 'Learners',
  'nav.users': 'Users',
  'nav.group_workspace': 'Workspace',
  'nav.group_administration': 'Administration',

  // Authentication & Login
  'auth.sign_in': 'Sign In',
  'auth.welcome_back': 'Sign in to Trainova',
  'auth.sign_in_subtitle': 'Sign in to access your AI simulation workspace',
  'auth.sign_in_to_org': 'Sign in to {{org}}',
  'auth.org_portal_welcome': 'Welcome to the {{org}} organization portal.',
  'auth.single_tenant_workspace': 'Single-tenant workspace',
  'auth.change_org': 'Change',
  'auth.change_org_title': 'Switch to default login',
  'auth.locked_by_url': 'Locked by URL',
  'auth.studio_badge': 'AI Simulation Studio',
  'auth.quick_demo_access': 'Quick Demo Access',
  'auth.demo_role_selector_aria': 'Demo role selector',
  'auth.role_learner': 'Learner',
  'auth.role_admin': 'Admin',
  'auth.role_super_admin': 'Super Admin',
  'auth.email_label': 'Email Address / User ID',
  'auth.email_placeholder': 'learner@enterprise.com',
  'auth.password_label': 'Password',
  'auth.password_placeholder': '••••••••••••',
  'auth.show_password_aria': 'Show password',
  'auth.hide_password_aria': 'Hide password',
  'auth.tenant_slug_label': 'Organization Slug',
  'auth.tenant_slug_placeholder': 'e.g. demo-trainova',
  'auth.remember_me': 'Keep me signed in on this device',
  'auth.forgot_password': 'Forgot password?',
  'auth.signing_in': 'Signing In...',
  'auth.submit_sign_in': 'Sign In',
  'auth.submit_sign_in_to_org': 'Sign In to {{org}}',
  'auth.entering_portal': 'Entering Trainova.ai…',
  'auth.bottom_note': 'Enterprise assessment portal · Accounts provisioned by your administrator',
  'auth.switch_account': 'Switch Account',
  'auth.logged_in_as': 'Logged in as {{name}}',
  'auth.login_error': 'Invalid email or password. Please try again.',
  'auth.session_expired': 'Your session has expired. Please sign in again.',
  'auth.err_required': 'Please enter your email and password.',
  'auth.err_invalid_credentials': 'Invalid email or password. Please check your credentials or click a Quick Demo role above.',
  'auth.err_login_failed': 'Login failed. Please check your credentials.',
  'auth.err_unexpected': 'An unexpected error occurred. Please try again.',
  'auth.reset_password_title': 'Reset Password',
  'auth.reset_password_msg': 'Password reset instructions have been dispatched to your email.',

  // Auth & Branding Panel (.auth-brand)
  'auth.brand_tagline_main': 'Practice the conversation',
  'auth.brand_tagline_em': "before it's real.",
  'auth.brand_lede': "Rehearse customer calls with AI personas, get scored on every competency, and prove you're ready.",
  'auth.brand_sim_who_name': 'Priya Sharma · Customer',
  'auth.brand_sim_who_desc': 'FinTech · Disputed Card Charge · Service',
  'auth.brand_sim_live': 'Practice',
  'auth.brand_sim_cust_role': 'Customer',
  'auth.brand_sim_cust_msg': "I've been charged twice for the same order and nobody's helped me for three days!",
  'auth.brand_sim_me_role': 'You (Learner)',
  'auth.brand_sim_me_msg': 'I understand your frustration, Priya. Let me pull up both transactions and investigate right now.',
  'auth.brand_sim_meter_empathy': 'Empathy',
  'auth.brand_sim_meter_resolution': 'Resolution',
  'auth.brand_sim_meter_readiness': 'Readiness',
  'auth.brand_flow_practice': 'Practice',
  'auth.brand_flow_assess': 'Assess',
  'auth.brand_flow_coach': 'Coach',
  'auth.brand_flow_prove': 'Prove Readiness',

  // Auth Layout Topline & Footer
  'auth.org_logo_alt': 'Organization Logo',
  'auth.footer_copyright': '© 2026 Trainova AI Inc.',
  'auth.footer_privacy': 'Privacy Policy',
  'auth.footer_terms': 'Terms of Service',
  'auth.footer_support': 'Support',

  // Login Gateway Transition
  'gateway.security_auth_title': 'Security & Auth',
  'gateway.security_auth_sub': 'Verifying session tokens',
  'gateway.tenant_gateway_title': 'Tenant Gateway',
  'gateway.tenant_gateway_sub_workspace': '{{tenant}} workspace',
  'gateway.tenant_gateway_sub_default': 'Enterprise tenant',
  'gateway.ai_core_title': 'AI Persona Core',
  'gateway.ai_core_sub': 'Syncing simulations & models',
  'gateway.ready_title': 'Ready',
  'gateway.ready_sub': 'Launching portal',
  'gateway.welcome_back': 'Welcome back, {{name}}!',
  'gateway.entering_portal': 'Entering Portal…',
  'gateway.status_authenticating': 'Authenticating session…',
  'gateway.status_connecting': 'Connecting to workspace…',
  'gateway.status_preparing': 'Loading AI simulation engines…',
  'gateway.status_ready': 'Ready! Launching dashboard…',

  // Dashboard
  'dashboard.greeting': 'Welcome back, {{name}}',
  'dashboard.subtitle': 'Rehearse high-stakes workplace simulations with realistic AI personas.',
  'dashboard.assigned_simulations': 'Assigned Simulations',
  'dashboard.practice_simulations': 'Practice & Explore Scenarios',
  'dashboard.recent_attempts': 'Recent Attempts',
  'dashboard.stats_completed': 'Completed Simulations',
  'dashboard.stats_avg_score': 'Average Score',
  'dashboard.stats_hours_practiced': 'Practice Time',
  'dashboard.stats_readiness': 'Role Readiness',
  'dashboard.view_all': 'View All',
  'dashboard.start_simulation': 'Start Simulation',
  'dashboard.resume_attempt': 'Resume Attempt',
  'dashboard.view_results': 'View Results',
  'dashboard.no_assigned': 'No simulations assigned at this time.',
  'dashboard.no_attempts': 'No simulation attempts recorded yet.',

  // Scenario Briefing & System Check
  'briefing.title': 'Scenario Briefing',
  'briefing.overview': 'Context & Situation',
  'briefing.objectives': 'Learning Objectives & Competencies',
  'briefing.persona': 'AI Persona Information',
  'briefing.role': 'Persona Role',
  'briefing.temperament': 'Temperament & Demeanor',
  'briefing.hardware_check': 'Hardware Readiness Check',
  'briefing.mic_status': 'Microphone',
  'briefing.camera_status': 'Camera (Optional)',
  'briefing.audio_status': 'Speaker Audio',
  'briefing.testing_mic': 'Speak into your microphone to test volume...',
  'briefing.mic_ready': 'Microphone is active and ready',
  'briefing.mic_permission_needed': 'Microphone permission required to proceed',
  'briefing.allow_mic': 'Allow Microphone Access',
  'briefing.retry_mic': 'Retry Microphone',
  'briefing.consent_title': 'Recording & AI Evaluation Consent',
  'briefing.consent_body': 'This session will be recorded and transcribed for automated competency scoring, rubric evaluation, and trainer debriefs. Audio and video data remain securely isolated within your organization tenant.',
  'briefing.consent_checkbox': 'I understand and consent to session recording and AI-assisted evaluation.',
  'briefing.start_simulation_btn': 'Enter Simulation Room',
  'briefing.preparing_room': 'Configuring AI Persona & Audio Stage...',

  // Simulation Room & Conversation Controls
  'simulation.live_title': 'Live AI Simulation Session',
  'simulation.persona_speaking': 'AI Persona is speaking...',
  'simulation.listening_user': 'Listening to you... Speak naturally',
  'simulation.connecting': 'Establishing secure audio stream...',
  'simulation.paused': 'Simulation paused',
  'simulation.mute_mic': 'Mute Mic',
  'simulation.unmute_mic': 'Unmute Mic',
  'simulation.finish_session': 'Complete Session',
  'simulation.retry': 'Retry Simulation',
  'simulation.turn_count': 'Turn {{current}}',
  'simulation.timer': 'Session Time',
  'simulation.transcript_title': 'Realtime Conversation Transcript',
  'simulation.transcript_empty': 'Conversation transcript will appear here as you speak.',
  'simulation.confirm_finish_title': 'End and Submit Simulation?',
  'simulation.confirm_finish_body': 'Are you sure you want to conclude this simulation? Your conversation will be submitted immediately for multi-dimensional AI rubric evaluation.',
  'simulation.confirm_cancel': 'Continue Simulation',
  'simulation.confirm_submit': 'Submit for Evaluation',
  'simulation.evaluating_title': 'Evaluating Conversation Performance...',
  'simulation.evaluating_subtitle': 'Analyzing regulatory compliance, customer empathy, resolution effectiveness, and objection handling.',

  // Autoplay & Voice Interaction Alerts
  'simulation.autoplay_blocked_banner': 'Click anywhere or tap Play Voice to enable AI persona audio',
  'simulation.play_audio_btn': 'Play Voice',

  // Microphone & Hardware Alerts in Simulation
  'simulation.mic_blocked_title': 'Microphone Access Blocked',
  'simulation.mic_blocked_banner': 'Microphone access is blocked by your browser. Click the lock/settings icon in your address bar to allow microphone access, or switch to Text Mode to continue.',
  'simulation.mic_blocked_help': 'To allow: Click the 🔒 or 🎛️ icon next to the URL in your browser bar, set Microphone to "Allow", and click Retry Mic.',
  'simulation.mic_help_btn': 'ℹ️ Help',
  'simulation.retry_mic': 'Retry Mic',
  'simulation.use_text_mode': 'Use Text Mode',
  'simulation.text_mode_active': 'Text Mode Active — Type your response below',
  'simulation.text_input_placeholder': 'Type your response here and press Enter...',
  'simulation.text_input_ai_speaking': '{{name}} is speaking...',
  'simulation.text_input_ai_thinking': 'AI persona is evaluating...',
  'simulation.text_send': 'Send',
  'simulation.switch_to_voice': 'Switch back to voice mode',
  'simulation.mic_live': 'Live',
  'simulation.mic_muted': 'Muted',
  'simulation.camera_live': 'Live',
  'simulation.camera_off': 'Off',
  'simulation.auto_saved_long': 'Auto-saved · resume if disconnected',
  'simulation.auto_saved_short': 'Auto-saved',
  'simulation.attempt_progress': '{{category}} · Attempt {{current}} of {{max}}',
  'simulation.rec_badge': 'REC',
  'simulation.init_badge': 'INIT',
  'simulation.nocam_badge': 'NO-CAM',
  'simulation.time_left': 'Time left',
  'simulation.elapsed': 'Elapsed',
  'simulation.stage_label': 'Stage',
  'simulation.stage_progress': '{{current}} of {{total}} · {{name}}',
  'simulation.live_llm_trace': 'Live LLM Trace',
  'simulation.inspect_trace_tooltip': 'Inspect real-time LLM API calls, system prompts, and dialogue states',
  'simulation.audio_error_desc': 'Voice synthesis will be used if available, or you can retry playback below.',
  'simulation.retry_audio': 'Retry Audio',
  'simulation.buffering_audio': 'Buffering audio…',
  'simulation.persona_thinking': 'Thinking...',
  'simulation.repeat': 'Repeat',
  'simulation.repeat_persona_tooltip': "Repeat {{name}}'s latest response",
  'simulation.ai_voice_chip': 'AI Voice',
  'simulation.ai_voice_tooltip': "This persona's voice is AI-generated",
  'simulation.camera_off_overlay': 'Camera off',
  'simulation.speaking_badge': 'Speaking',
  'simulation.you_label': 'You',
  'simulation.scenario_heading': 'Scenario',
  'simulation.scenario_hide_tip': 'Click to hide briefing',
  'simulation.scenario_view_tip': 'Click to view full scenario briefing',
  'simulation.hide': 'Hide',
  'simulation.show': 'Show',
  'simulation.your_objective': 'Your Objective',
  'simulation.scenario_personas': 'Scenario Personas',
  'simulation.live_transcript': 'Live transcript',
  'simulation.live_badge': 'Live',
  'simulation.transcript_listening': 'Listening for conversation start...',
  'simulation.role_learner': 'Learner',
  'simulation.role_ai_prefix': 'AI · {{role}}',
  'simulation.repeat_message_tooltip': 'Repeat this message',
  'simulation.participants_single': '1 AI agent ({{name}}) + you',
  'simulation.participants_multiple': '{{count}} AI personas + you',
  'simulation.toggle_mic_tooltip': 'Toggle Microphone',
  'simulation.toggle_camera_tooltip': 'Toggle Camera',
  'simulation.camera_on': 'Camera on',
  'simulation.cam_on_short': 'Cam on',
  'simulation.cam_off_short': 'Cam off',
  'simulation.repeat_last_tooltip': 'Repeat last message',
  'simulation.repeat_last': 'Repeat last',
  'simulation.transcript_tooltip': 'View full conversation transcript',
  'simulation.transcript': 'Transcript',
  'simulation.end_assessment_tooltip': 'End assessment',
  'simulation.end_assessment': 'End assessment',
  'simulation.end_short': 'End',
  'simulation.end_modal_title': 'End this simulation?',
  'simulation.end_modal_desc': "Your audio, video, and transcript will be submitted for scoring and feedback. You won't be able to continue this attempt.",
  'simulation.end_modal_time_elapsed': 'Time elapsed',
  'simulation.end_modal_turns_recorded': 'Turns recorded',
  'simulation.end_modal_turns_count': '{{count}} turns',
  'simulation.end_modal_continue': 'Continue simulation',
  'simulation.end_modal_submit': 'End and submit',
  'simulation.turn_ended_complete_title': 'Conversation Complete',
  'simulation.turn_ended_limit_title': 'Turn Limit Reached',
  'simulation.turn_ended_desc': 'Review and proceed to score report',
  'simulation.turn_ended_view_report': 'View Score Report',
  'simulation.trace_modal_title': 'Inspect LLM API Calls, Prompts & Dialogue States',
  'simulation.trace_modal_close': 'Close',
  'simulation.dismiss_banner': 'Dismiss banner',
  'simulation.role_ai': 'AI',
  'simulation.mic_permission_help_title': 'Microphone permission help',
  'simulation.switch_to_voice_title': 'Switch back to voice mode',
  'simulation.privacy_video_notice': 'Observations are processed 100% locally in your browser. Raw video is never sent to the server.',
  'simulation.live_learner_speaking': 'Speaking...',
  'simulation.ready_to_begin': 'Ready to begin dialogue',
  'simulation.ready_to_begin_desc': 'Speak through your microphone or type a response in the quick input box below.',
  'simulation.replay': 'Replay',
  'simulation.return_to_stage': 'Return to Simulation Stage',
  'simulation.trace_session_attempt': 'Session Attempt',
  'simulation.trace_target_persona': 'Target Persona',
  'simulation.trace_stage_progression': 'Stage Progression',
  'simulation.trace_language_turns': 'Language & Turns',
  'simulation.trace_turns_count': '{{count}} turns',
  'simulation.trace_live_exchange_log': 'Live Turn Exchange Log ({{count}} turns)',
  'simulation.trace_realtime_stream': 'Real-time dialogue stream',
  'simulation.trace_no_turns': 'No turns processed yet. Say or type your greeting to initiate dialogue.',
  'simulation.submit_response': 'Submit Response',
  'simulation.submit_now': 'Submit Now',
  'simulation.listening': 'Listening...',
  'simulation.finalizing_response': 'Finalizing response…',
  'simulation.auto_submitting_in': 'Auto-submitting in {{seconds}}s…',
  'simulation.speak_to_continue': 'Speak to continue or submit now',
  'simulation.submitting_countdown': 'Submitting in {{seconds}}s',

  // Simulation Completion & Results Generation Popup
  'simulation.eval_overlay_title': 'Analyzing & Preparing Results',
  'simulation.eval_overlay_desc': "Your simulation has ended. Trainova's multi-dimension assessment engine is synthesizing dialogue turns, assessing competencies, and preparing your comprehensive scorecard...",
  'simulation.eval_mic_video_stopped': 'Microphone & video capture stopped',
  'simulation.eval_mic_stopped': 'Microphone capture stopped',
  'simulation.eval_transcript_captured': 'Session transcript captured ({{count}} turns)',
  'simulation.eval_generating_scorecard': 'Generating rubric assessment & behavioral scorecard...',
  'simulation.eval_stuck_desc': 'This is taking longer than usual. You can keep waiting here, or open the attempt and read the scorecard once it finishes.',
  'simulation.eval_open_attempt_btn': 'Open my attempt →',

  // Simulation Completion Banner & Dialogue End Modals
  'simulation.turns_completed_summary': 'Simulation turns completed ({{count}} turns).',
  'simulation.ready_for_debrief': 'Ready for evaluation debrief.',
  'simulation.turn_ended_limit_desc': 'You have reached the maximum allowed conversation turns ({{current}} of {{max}} turns) for this simulation.',
  'simulation.turn_ended_concluded_desc': 'Your dialogue partner has concluded the conversation ({{count}} turns recorded).',

  // Room Initialization Screen & Readiness Stepper
  'simulation.initializing_room_title': 'Initializing Simulation Room...',
  'simulation.init_slow_desc': 'Connecting to the AI roleplay engine is taking longer than usual. You can wait a moment or trigger a reconnect.',
  'simulation.init_loading_desc': 'Loading scenario briefing, persona directives & starting dialogue.',
  'simulation.init_briefing_loaded': 'Scenario briefing & persona directives',
  'simulation.init_dialogue_connected': 'Dialogue engine connected',
  'simulation.init_mic_ready': 'Microphone ready for barge-in',
  'simulation.init_audio_loaded': 'Required audio assets loaded & decoded',
  'simulation.init_ready': 'Ready',
  'simulation.init_waiting': 'Waiting',
  'simulation.mic_blocked_init_title': 'Microphone access is blocked',
  'simulation.mic_blocked_init_desc': "Allow microphone access in your browser's address bar, then retry. If no prompt appears, this browser or connection may not support microphone capture — the room can still open with typed replies.",
  'simulation.requesting_access': 'Requesting access…',
  'simulation.retry_mic_init': 'Retry microphone',
  'simulation.mic_checking_desc': 'Allow microphone access if your browser asks — you can speak the moment the room opens.',
  'simulation.reconnect_room': 'Reconnect Room',
  'simulation.back_to_briefing': 'Back to briefing',
  'simulation.exit_to_briefing': 'Exit to briefing',
  'simulation.unable_to_start': 'Unable to Start Simulation',

  // Role & Mood Lookups
  'role.customer': 'Customer',
  'role.client': 'Client',
  'role.manager': 'Manager',
  'role.colleague': 'Colleague',
  'role.learner': 'Learner',
  'mood.irritated_polite': 'Mildly irritated but polite',
  'mood.frustrated': 'Frustrated',
  'mood.satisfied': 'Satisfied',
  'mood.neutral': 'Neutral',
  'mood.anxious': 'Anxious',
  'mood.demanding': 'Demanding',

  // Evaluation Scorecard & Results
  'scorecard.title': 'Assessment Evaluation Scorecard',
  'scorecard.overall_score': 'Overall Performance Score',
  'scorecard.passing_score': 'Passing Standard: {{score}}%',
  'scorecard.status_passed': 'Passed',
  'scorecard.status_failed': 'Needs Improvement',
  'scorecard.status_in_progress': 'Evaluating',
  'scorecard.competencies_title': 'Evaluated Core Competencies',
  'scorecard.key_strengths': 'Key Strengths Demonstrated',
  'scorecard.areas_for_growth': 'Targeted Growth Opportunities',
  'scorecard.actionable_recommendations': 'Actionable Coaching Recommendations',
  'scorecard.evidence_quotes': 'Verbatim Transcript Evidence',
  'scorecard.replay_recording': 'Assessment Session Recording & Replay',
  'scorecard.download_report': 'Download PDF Report',
  'scorecard.retry_btn': 'Retry Simulation',
  'scorecard.back_dashboard': 'Back to Dashboard',
  'scorecard.retake_simulation': 'Retake Simulation',
  'scorecard.reevaluate_openai': 'Re-evaluate with OpenAI',
  'scorecard.evaluating_action': 'Evaluating...',
  'scorecard.practice_similar': 'Practice Similar',
  'scorecard.download_json': 'Download Report JSON',
  'scorecard.inspect_prompts': 'Inspect LLM Prompts',
  'scorecard.attempt_counter': 'Attempt {{current}} of {{max}}',
  'scorecard.target_passing_score': 'Target Passing Score: {{score}}%',
  'scorecard.role_label': 'Role:',
  'scorecard.persona_label': 'Persona:',
  'scorecard.scorecard_title_suffix': 'Scorecard',
  'scorecard.badge_passed': 'Passed',
  'scorecard.badge_needs_review': 'Needs Review',
  'scorecard.badge_completed': 'Completed',
  'scorecard.badge_ai_eval': 'AI Evaluation',
  'scorecard.all_attempts_btn': '‹ All Attempts',
  'scorecard.analyzing_attempt': 'Analyzing Attempt Performance...',
  'scorecard.evaluating_subtext': 'Evaluating speech delivery, conversational rubrics, and generating behavioral coaching feedback.',
  'scorecard.in_progress_title': 'Attempt Is In Progress',
  'scorecard.in_progress_desc': 'This simulation attempt has not yet been submitted for evaluation. You can resume and finish the dialogue session, or submit it now to generate your evaluation report.',
  'scorecard.resume_simulation': 'Resume Simulation',
  'scorecard.end_and_submit': 'End & Submit for Evaluation',
  'scorecard.submitting_evaluating': 'Submitting & Evaluating...',
  'scorecard.my_attempts': 'My Attempts',
  'scorecard.assessment_completed_title': 'Assessment Completed',
  'scorecard.session_recording_title': 'Your Session Recording',
  'scorecard.dashboard_btn': 'Dashboard',
  'scorecard.overall_score_label': 'Overall score',
  'scorecard.below_target': 'Below Target',
  'scorecard.target_achieved': 'Target Achieved',
  'scorecard.pass_mark_desc': 'Pass mark {{score}}%. Review the coaching feedback below and start your next attempt to achieve readiness.',
  'scorecard.readiness_label': 'READINESS',
  'scorecard.target_label': 'Target',
  'scorecard.time_taken_label': 'TIME TAKEN',
  'scorecard.of_time': 'of {{time}}',
  'scorecard.stages_completed_label': 'STAGES COMPLETED',
  'scorecard.stages_of': '{{current}} of {{total}}',
  'scorecard.all_stages_reached': 'All conversation stages reached',
  'scorecard.evaluated_label': 'EVALUATED',
  'scorecard.overall_feedback_title': 'Overall feedback',
  'scorecard.ai_summary_reviewed': 'AI summary · reviewed by coach',
  'scorecard.overall_feedback_empty': 'Overall feedback will appear here once the evaluation is complete.',
  'scorecard.based_on_rubric': "Based on the recording, transcript and your organisation's rubric.",
  'scorecard.top_strength_tag': 'TOP STRENGTH',
  'scorecard.biggest_gap_tag': 'BIGGEST GAP',
  'scorecard.delivery_tag': 'DELIVERY',
  'scorecard.speaking_pace_measured': 'Speaking pace measured',
  'scorecard.metric_breakdown_empty': 'Metric breakdowns will appear after evaluation.',
  'scorecard.competency_breakdown_title': 'Competency breakdown',
  'scorecard.scored_against_rubric': "Scored against your organisation's rubric",
  'scorecard.legend_strong': 'Strong (4.0+)',
  'scorecard.legend_ontrack': 'On-track (3.0 - 3.9)',
  'scorecard.legend_needswork': 'Needs work (below 3.0)',
  'scorecard.no_competencies_evaluated': 'No competency rubrics were evaluated for this session.',
  'scorecard.weight_pct': 'Weight {{pct}}%',
  'scorecard.score_calc_traceability': 'Score Calculation & Weight Traceability',
  'scorecard.formula_label': 'Formula:',
  'scorecard.table_competency': 'Competency',
  'scorecard.table_raw_score': 'Raw Score',
  'scorecard.table_weight': 'Weight',
  'scorecard.table_contribution': 'Contribution',
  'scorecard.total_calc_score': 'Total Calculated Score:',
  'scorecard.pass_threshold': 'Pass Threshold: {{score}}%',
  'scorecard.evidence_traceability_prefix': 'Evidence Traceability:',
  'scorecard.criterion_not_met': 'Criterion not met during conversation.',
  'scorecard.improvement_tips_title': 'Improvement tips',
  'scorecard.most_impact_first': 'Most impact first',
  'scorecard.improvement_tips_empty': 'Improvement tips will appear here once the AI evaluation is complete.',
  'scorecard.actionable_recommendation_label': 'Actionable Recommendation:',
  'scorecard.camera_analysis_title': 'Camera Video Analysis',
  'scorecard.audio_only_scenario': 'Audio-Only Scenario',
  'scorecard.audio_only_desc': 'This simulation is configured as an audio-only assessment. Camera feed was not requested or recorded for this scenario.',
  'scorecard.assessment_mode_voice': 'Assessment Mode: Voice Only',
  'scorecard.scoring_focus_empathy': 'Scoring Focus: Empathy, Tone & Communication',
  'scorecard.privacy_no_camera': 'Privacy: No camera feed recorded',
  'scorecard.not_recorded': 'Not Recorded',
  'scorecard.video_processing': 'Video Processing',
  'scorecard.video_processing_sub': 'Computer vision analysis is running in the background. Metrics will update automatically.',
  'scorecard.partial_analysis': 'Partial Analysis',
  'scorecard.analysis_completed': 'Analysis Completed',
  'scorecard.cv_observations_sub': "Local AI computer vision observations derived from the learner's camera stream.",
  'scorecard.face_visible': 'Face Visibility',
  'scorecard.continuous_presence': 'Continuous presence in camera frame',
  'scorecard.conversation_quality_title': 'Conversation quality',
  'scorecard.how_customer_felt_sub': 'How customer felt throughout the call',
  'scorecard.sentiment_over_call': 'Customer sentiment over the call',
  'scorecard.sentiment_satisfied': 'Satisfied',
  'scorecard.sentiment_neutral': 'Neutral',
  'scorecard.sentiment_upset': 'Upset',
  'scorecard.start_time': 'Start',
  'scorecard.end_time': 'End',
  'scorecard.sentiment_data_empty': 'Sentiment timeline will appear once conversation turns are evaluated.',
  'scorecard.required_steps_title': 'Required steps (compliance)',
  'scorecard.completed_of_total': '{{done}} of {{total}} completed',
  'scorecard.compliance_steps_empty': 'No compliance steps were defined for this scenario.',
  'scorecard.step_done': 'Done',
  'scorecard.step_missed': 'Missed',
  'scorecard.scroll_to_view_steps': 'Scroll to view all {{count}} required steps',
  'scorecard.coach_comments_title': 'Coach comments',
  'scorecard.comments_count_single': '1 comment',
  'scorecard.comments_count_plural': '{{count}} comments',
  'scorecard.reply_placeholder': 'Reply to coach notes...',
  'scorecard.reply_btn': 'Reply',
  'scorecard.evaluation_unavailable': 'Evaluation Results Unavailable',
  'scorecard.retry_fetch': 'Retry Fetch',

  // Video & Recording Replay
  'video.session_recording': 'Assessment Session Recording',
  'video.learner_cam': 'Learner Video Feed',
  'video.persona_cam': 'AI Persona Feed',
  'video.play': 'Play',
  'video.pause': 'Pause',
  'video.restart': 'Restart',
  'video.playback_speed': 'Speed',
  'video.recording_unavailable': 'Session recording is still encoding or not available.',
  'video.recording_audio_only': 'Audio Only Session',

  // Tenant Admin & Settings
  'admin.languages_title': 'Languages & Regional Locales',
  'admin.languages_subtitle': 'Configure supported languages, default locale, and translation access for your organization.',
  'admin.supported_languages': 'Enabled Organization Languages',
  'admin.default_language': 'Tenant Default Language',
  'admin.fallback_language': 'Fallback Language',
  'admin.onboarding_language': 'Default Onboarding Language',
  'admin.allow_user_selection': 'Allow learners and staff to switch display language',
  'admin.allow_user_selection_help': 'When enabled, users will see the language selector in their navigation bar.',
  'admin.test_preview_title': 'Translation Provider Test & Live Preview',
  'admin.test_input_label': 'Sample Text to Translate',
  'admin.test_target_label': 'Target Language',
  'admin.test_run_btn': 'Test Translation API',
  'admin.test_result_label': 'Translation Output',
  'admin.save_settings': 'Save Language Settings',
  'admin.saved_success': 'Language configuration saved successfully.',
  'admin.provider_google': 'Google Cloud Translation Provider (Active)',
  'admin.provider_cached': 'Cached in Multi-Tenant Translation Ledger',
  'admin.console_title': 'Trainova AI Administration Console',

  // Navigation Extras
  'nav.group_account': 'Account & Support',
  'nav.learners_users': 'Learners & Users',
  'nav.organizations': 'Organizations',
  'nav.rubrics': 'Evaluation Rubrics',
  'nav.llm_logs': 'LLM Audit & Logs',
  'nav.audit_trail': 'Security Audit Trail',

  // Page Titles
  'dashboard.assigned_studio': 'Assigned Simulations & Practice Studio',
  'dashboard.search_placeholder': 'Search scenarios, personas...',
  'attempts.title_admin': 'Simulation Attempts & Performance Ledger',
  'attempts.title_learner': 'My Simulation Attempts & History',
  'attempts.history_title': 'Scenario Attempt History',
  'scenarios.studio_title': 'Scenario & Simulation Studio',
  'users.directory_title': 'Organization Members & User Directory',
  'tenants.title': 'Platform Organizations & Tenants',
  'audit.title': 'Enterprise Audit Trail & Governance Logs',
  'llm_logs.title': 'LLM Engine Logs & Model Inference Auditing',
  'reports.title': 'Enterprise Reports & Cohort Analytics',
  'analytics.title': 'Analytics & Practice Intelligence',

  // Breadcrumbs
  'breadcrumb.home': 'Home',
  'breadcrumb.dashboard': 'Dashboard',
  'breadcrumb.admin': 'Admin',
  'breadcrumb.learner_portal': 'Learner Portal',
  'breadcrumb.administration_console': 'Administration Console',
  'breadcrumb.simulation_attempts': 'Simulation Attempts',
  'breadcrumb.my_simulation_attempts': 'My Simulation Attempts',
  'breadcrumb.my_attempts': 'My Attempts',
  'breadcrumb.attempt_history': 'Attempt History',
  'breadcrumb.assigned_simulations': 'Assigned Simulations',
  'breadcrumb.scenarios_studio': 'Scenario Studio',
  'breadcrumb.scenarios': 'Scenarios',
  'breadcrumb.scenario_details': 'Scenario Details',
  'breadcrumb.create_scenario': 'Create Scenario',
  'breadcrumb.edit_scenario': 'Edit Scenario',
  'breadcrumb.user_directory': 'User Directory',
  'breadcrumb.learners_users': 'Learners & Users',
  'breadcrumb.client_organizations': 'Client Organizations',
  'breadcrumb.org_details': 'Organization Details',
  'breadcrumb.evaluation_rubrics': 'Evaluation Rubrics',
  'breadcrumb.evaluation_config': 'Evaluation Configuration',
  'breadcrumb.evaluation_studio': 'Evaluation Studio',
  'breadcrumb.reports_analytics': 'Reports & Analytics',
  'breadcrumb.reports': 'Reports',
  'breadcrumb.analytics': 'Analytics',
  'breadcrumb.audit_log': 'Audit Log',
  'breadcrumb.audit_trail': 'Security Audit Trail',
  'breadcrumb.llm_audit_trail': 'LLM Audit Trail',
  'breadcrumb.llm_logs': 'LLM Logs',
  'breadcrumb.tenant_settings': 'Tenant Settings',
  'breadcrumb.notifications': 'Notification Settings',
  'breadcrumb.custom_fields': 'Custom User Fields',
  'breadcrumb.packages': 'Package Management',

  // Table Headers & Controls
  'table.simulation': 'Simulation',
  'table.category': 'Category',
  'table.status': 'Status',
  'table.time_limit': 'Time Limit',
  'table.attempts_left': 'Attempts Left',
  'table.progress': 'Progress',
  'table.actions': 'Actions',
  'table.member': 'Member',
  'table.emp_id': 'Emp ID',
  'table.designation': 'Designation',
  'table.reporting_manager': 'Reporting Manager',
  'table.team': 'Team',
  'table.region': 'Region',
  'table.role': 'Role',
  'table.attempt_num': 'Attempt #',
  'table.started': 'Started',
  'table.duration': 'Duration',
  'table.turns': 'Turns',
  'table.score': 'Score',
  'table.ai_persona': 'AI Persona',
  'table.difficulty': 'Difficulty',
  'table.scenario': 'Scenario',
  'table.organization': 'Organization',
  'table.slug': 'Slug',
  'table.users': 'Users',
  'table.scenarios': 'Scenarios',
  'table.attempts': 'Attempts',
  'table.created': 'Created',
  'table.timestamp': 'Timestamp',
  'table.action': 'Action',
  'table.actor': 'Actor',
  'table.target': 'Target',
  'table.origin_ip': 'Origin / IP',
  'table.payload': 'Payload',
  'table.no_records': 'No records found',
  'table.no_attempts': 'No attempts found',
  'table.no_attempts_desc': 'No attempts have been recorded for this scenario yet.',
  'table.no_users': 'No matching users found.',
  'table.no_orgs': 'No organizations found',
  'table.no_orgs_desc': 'Try modifying your search query or status filter.',
  'table.view_details': 'View Details',
  'table.filter_all': 'All',
  'table.filter_not_started': 'Not Started',
  'table.filter_in_progress': 'In Progress',
  'table.filter_completed': 'Completed',
  'table.filter_all_statuses': 'All Statuses',
  'table.filter_all_categories': 'All Categories',
  'table.filter_all_difficulties': 'All Difficulties',
  'table.published': 'Published',
  'table.draft': 'Draft',
  'table.archived': 'Archived',
  'table.beginner': 'Beginner',
  'table.intermediate': 'Intermediate',
  'table.advanced': 'Advanced',
  'table.sort_latest': 'Sort: Latest',
  'table.sort_title': 'Sort: Title',
  'table.sort_duration': 'Sort: Duration',

  // Badges
  'badge.enterprise_control_plane': 'Enterprise Control Plane',
  'badge.roleplay_hub': 'Roleplay Hub',
  'badge.practice_ledger': 'Practice Ledger',
  'badge.admin_practice_ledger': 'Admin Practice Ledger',
  'badge.simulation_authoring': 'Simulation Authoring',
  'badge.access_governance': 'Access & Governance',
  'badge.multi_tenant_master': 'Multi-Tenant Master',
  'badge.evaluation_governance': 'Evaluation Governance',
  'badge.executive_intelligence': 'Executive Intelligence',
  'badge.security_compliance': 'Security & Compliance',
  'badge.ai_observability': 'AI Observability',

  // Common UI Actions & Statuses
  'common.save': 'Save',
  'common.cancel': 'Cancel',
  'common.close': 'Close',
  'common.delete': 'Delete',
  'common.edit': 'Edit',
  'common.loading': 'Loading...',
  'common.loading_records': 'Loading history records…',
  'common.submitting': 'Submitting...',
  'common.error': 'An unexpected error occurred. Please try again.',
  'common.retry': 'Retry',
  'common.success': 'Success',
  'common.status': 'Status',
  'common.active': 'Active',
  'common.disabled': 'Disabled',
  'common.optional': 'Optional',
  'common.required': 'Required',
  'common.offline_notice': 'You are currently offline. Displaying cached data and local translation resources.',
  'common.learn_more': 'Learn More',
  'common.back': 'Back',

  // Hero Descriptions
  'desc.select_scenario': 'Select an assessment scenario to begin or continue your conversational roleplay',
  'desc.attempts_admin': 'Monitor learner simulations across organizations, inspect attempt scorecards, and audit historical practice outcomes.',
  'desc.attempts_learner': 'Track your simulation sessions, resume active scenarios, and review past practice scorecards.',
  'desc.scenarios': 'Author, test, and publish conversational AI simulations, customer personas, and evaluation rubrics.',
  'desc.tenants': 'Centralized management of enterprise client organizations, tenant provisioning, and platform governance.',
  'desc.users': 'Manage learner accounts, assign instructor and manager permissions, and oversee cohort access.',
  'desc.audit': 'Inspect immutable compliance events, administrative mutations, and authentication history.',
  'desc.llm_audit': 'Audit LLM prompts, input/output token consumptions, model latencies, costs, and raw payloads per attempt and scenario.',
  'desc.reports': 'Review organization pass rates, competency benchmark bars, learner metrics, CSV/JSON data dumps.',
  'desc.analytics': 'Real-time simulation completion metrics, competency pass rates, learner drilldowns, and multi-tenant performance.',
  'desc.admin_dashboard': 'System-wide governance, tenant administration, training metrics, and AI simulation studio.',
  'desc.llm_config': 'Configure dialogue LLMs, scoring engines, STT/TTS voice models, and encrypted API keys.',
  'desc.udf_settings': 'Configure learner metadata fields (region, cost center, BU), CSV validation rules, and API sync mappings.',
  'desc.eval_config': 'Configure evaluation rubrics, scoring prompts, golden set calibration, and publishing lifecycles.',

  // Dashboard Stats & Cards
  'dashboard.stats.total_simulations': 'Total Simulations',
  'dashboard.stats.in_progress': 'In Progress',
  'dashboard.stats.completed': 'Completed',
  'dashboard.stats.not_started': 'Not Started',
  'dashboard.facts.time_limit': 'Time Limit',
  'dashboard.facts.mins': 'mins',
  'dashboard.facts.attempts': 'Attempts',
  'dashboard.facts.pass_benchmark': 'Pass Benchmark',
  'dashboard.facts.benchmark': 'Benchmark',
  'dashboard.facts.attempt_progress': 'Attempt progress',
  'dashboard.actions.briefing_and_start': 'Briefing & Start →',
  'dashboard.actions.resume_attempt': 'Resume Attempt →',
  'dashboard.actions.view_scorecard': 'View Scorecard',
  'dashboard.actions.max_attempts_reached': 'Max Attempts Reached',
  'dashboard.actions.available_soon': 'Available Soon',
  'dashboard.actions.simulation_closed': 'Simulation Closed',
  'dashboard.actions.briefing': 'Briefing →',
  'dashboard.actions.resume': 'Resume',
  'dashboard.actions.scorecard': 'Scorecard',

  // Status Labels
  'status.not_started': 'Not Started',
  'status.in_progress': 'In Progress',
  'status.completed': 'Completed',
  'status.assigned': '★ Assigned',
  'status.ready_to_practice': 'Ready to practice',
  'status.passed': 'Passed',
  'status.not_passed': 'Not passed',
  'status.paused': 'Paused',
  'status.evaluated': 'Evaluated',
  'status.expired': 'Expired',
  'status.abandoned': 'Abandoned',
  'status.unknown': 'Unknown',

  // Attempts Stats & Actions
  'attempts.stats.total_attempts': 'Total Attempts',
  'attempts.stats.expired_inactive': 'Expired / Inactive',
  'attempts.loading_title': 'Loading Practice Attempts...',
  'attempts.loading_desc': 'Retrieving simulation sessions, attempt outcomes, and historical practice logs.',
  'attempts.no_attempts_title': 'No attempts matching criteria',
  'attempts.no_attempts_filter_hint': 'Try changing or clearing your search query and status filters.',
  'attempts.no_attempts_empty_hint': 'You have not started any simulation attempts yet. Choose a scenario from the catalogue to begin practice.',
  'attempts.start_scenario_cta': 'Start a Scenario',
  'attempts.pagination_label': 'attempts',
  'attempts.tabs.all': 'All Attempts',
  'attempts.admin_ledger_badge': '🛡️ Admin Attempt Ledger',
  'attempts.all_organizations': '🏢 All Organizations (Global)',
  'attempts.search_organization_placeholder': 'Search organization by name or slug…',
  'attempts.no_organizations_match': 'No organizations match "{{query}}"',
  'attempts.view_scope_label': 'View Scope:',
  'attempts.all_learners': '👥 All Learners',
  'attempts.my_own_attempts': '👤 My Own Attempts',
  'attempts.search_learner_placeholder': 'Search learner by name or email…',
  'attempts.learners_in_scope': 'Learners in Scope ({{count}})',
  'attempts.no_learners_match': 'No learners match "{{query}}"',
  'attempts.loading_attempts': 'Loading simulation attempts...',
  'attempts.showing_count': 'Showing {{count}} simulation attempt',
  'attempts.showing_count_plural': 'Showing {{count}} simulation attempts',
  'attempts.search_placeholder': 'Search scenarios...',
  'attempts.meta.attempt': 'Attempt',
  'attempts.meta.difficulty': 'Difficulty',
  'attempts.meta.date': 'Date',
  'attempts.meta.evaluation': 'Evaluation',
  'attempts.meta.not_started': 'Not started',
  'attempts.view_scorecard': 'View Scorecard →',
  'attempts.view_attempt': 'View Attempt →',
  'attempts.all_attempts_link': 'All Attempts',
  'attempts.progress.turns': 'Turns Progress',
  'attempts.progress.turns_unit': 'turns',
  'attempts.progress.elapsed_time': 'Elapsed Time',
  'attempts.action.resuming': 'Resuming...',
  'attempts.action.resume_simulation': 'Resume Simulation',
  'attempts.err_resume_failed': 'Failed to resume attempt',
  'attempts.action.starting': 'Starting...',
  'attempts.action.retry_attempt': 'Retry Attempt',
  'attempts.action.quota_exhausted': 'Quota Exhausted',
  'attempts.action.attempts_left': 'left',
  'attempts.action.attempts_remaining': '{{count}} attempts remaining',
  'attempts.action.quota_reached': 'Attempt quota reached',
  'attempts.action.retry_simulation': 'Retry Simulation',
  'attempts.meta.quota': 'Quota',
  'attempts.meta.of': 'of',
  'attempts.history_title_default': 'Scenario Attempt History',
  'attempts.history_subtitle': 'Showing all recorded attempts and progression for this scenario.',
  'attempts.history_quota_used': '{{used}} / {{max}} used ({{remaining}} left)',

  // Attempts Strip (Results & Key Moments)
  'attempts.strip.your_attempts': 'YOUR ATTEMPTS',
  'attempts.strip.used_of_total': '{{used}} of {{total}} used',
  'attempts.strip.best_counts': 'Best score counts toward result',
  'attempts.strip.attempt_n': 'ATTEMPT {{n}}',
  'attempts.strip.best': 'BEST',
  'attempts.strip.viewing': 'VIEWING',
  'attempts.strip.available': 'Available',
  'attempts.strip.passed': '✓ Passed',
  'attempts.strip.needs_review': '✕ Needs Review',
  'attempts.strip.view_details': 'View details →',
  'attempts.strip.take_attempt': 'Take attempt',

  // Pagination
  'pagination.showing': 'Showing {{start}}–{{end}} of {{total}} {{items}}',
  'pagination.per_page': 'Per page:',
  'pagination.previous': '‹ Previous',
  'pagination.next': 'Next ›',
  'pagination.page_aria': 'Page {{page}}',
  'pagination.prev_aria': 'Previous page',
  'pagination.next_aria': 'Next page',
  'pagination.select_per_page_aria': 'Select items per page',
  'pagination.nav_aria': 'Pagination',

  // Difficulty & Category
  'difficulty.beginner': 'Beginner',
  'difficulty.intermediate': 'Intermediate',
  'difficulty.advanced': 'Advanced',
  'difficulty.standard': 'Standard',
  'category.customer_service': 'Customer Service',
  'category.sales': 'Sales',
  'category.compliance': 'Compliance',
  'category.de_escalation': 'De-escalation',
  'category.coaching': 'Coaching',
  'category.general_scenario': 'General Scenario',

  // Common Labels
  'common.organization_label': 'Organization:',
  'common.attempts': 'attempts',
  'nav.explore_scenarios': 'Explore Scenarios',
  'nav.learner_portal': 'Learner Portal',

  // Admin Overview KPI Stats
  'admin.stats.total_organizations': 'Total Client Organizations',
  'admin.stats.active_subscriptions': 'active enterprise subscriptions',
  'admin.stats.active_workspaces': 'Active Workspaces',
  'admin.stats.live_proctoring': 'Live & proctoring enabled',
  'admin.stats.org_members': 'Organization Members',
  'admin.stats.active_seats': 'active learner seats',
  'admin.stats.active_accounts': 'Active Accounts',
  'admin.stats.compliant_sso': 'Compliant & SSO verified',
  'admin.stats.published_cases': 'Published Simulation Cases',
  'admin.stats.total_authored_cases': 'total authored cases',
  'admin.stats.ai_health': 'AI Engine Health',
  'admin.stats.operational_100': '100% Operational',
  'admin.stats.models_online': 'Dialogue & rubric models online',

  // Admin Navigation Sections & Cards
  'admin.nav_section.org_governance': 'Organization & Access Governance',
  'admin.nav_section.training_analytics': 'Training Analytics & Reporting',
  'admin.nav_section.ai_studio': 'AI Simulation & Rubrics Studio',
  'admin.nav_section.customization': 'Customization & Configuration',
  'admin.nav.org_management': 'Organization Management',
  'admin.nav.org_management_desc': 'Provision and configure enterprise client tenants, brand guidelines, domains, and global limits.',
  'admin.nav.user_management': 'User & Role Management',
  'admin.nav.user_management_desc': 'Manage learner accounts, assign instructor and manager permissions, and oversee cohort access.',
  'admin.nav.security_audit': 'Security & Audit Logs',
  'admin.nav.security_audit_desc': 'Inspect immutable compliance events, administrative mutations, and authentication history.',
  'admin.nav.llm_audit': 'LLM Execution & Token Audit Trail',
  'admin.nav.llm_audit_desc': 'Audit LLM prompts, input/output token consumptions, model latencies, costs, and raw payloads per attempt and scenario.',
  'admin.nav.subscription_packages': 'Subscription Packages & Orders',
  'admin.nav.subscription_packages_desc': 'Configure SaaS packages, credit quotas, rollover rules, and approve tenant add-on orders.',
  'admin.nav.billing': 'Subscription, Credits & Add-Ons',
  'admin.nav.billing_desc': 'Track simulation creation credit balances, rollover status, and order add-on packages or voice passes.',
  'admin.nav.training_analytics': 'Enterprise Training Analytics',
  'admin.nav.training_analytics_desc': 'Real-time simulation completion metrics, competency pass rates, learner drilldowns, and multi-tenant performance.',
  'admin.nav.reports': 'Simulation & Competency Reports',
  'admin.nav.reports_desc': 'Review organization pass rates, competency benchmark bars, learner metrics, CSV/JSON data dumps.',
  'admin.nav.scenario_studio': 'Scenario & Case Studio',
  'admin.nav.scenario_studio_desc': 'Author, edit, test, and publish simulation cases, customer personas, and competency rubrics.',
  'admin.nav.evaluation_studio': 'AI Evaluation Studio',
  'admin.nav.evaluation_studio_desc': 'Configure evaluation rubrics, scoring prompts, golden set calibration, and publishing lifecycles.',
  'admin.nav.llm_vault': 'AI Provider & LLM Key Vault',
  'admin.nav.llm_vault_desc': 'Configure dialogue LLMs, scoring engines, STT/TTS voice models, and encrypted API keys.',
  'admin.nav.udf': 'Learner Custom Fields (UDF) & Mapping',
  'admin.nav.udf_desc': 'Configure learner metadata fields (region, cost center, BU), CSV validation rules, and API sync mappings.',
  'admin.nav.notifications': 'Notification & Email Settings',

  // Reports Filters
  'reports.filters': 'Filters',
  'reports.filter_simulation': 'Simulation:',
  'reports.select_simulation': 'Select Simulation…',
  'reports.search_sim_placeholder': 'Search simulation title, category, ID...',
  'reports.no_sims_found': 'No simulations found matching search',
  'reports.filter_organization': 'Organization:',
  'reports.all_organizations': 'All Organizations',
  'reports.search_org_placeholder': 'Search organization name, ID, slug...',
  'reports.all_orgs_cross_tenant': '🌐 All Organizations (Cross-Tenant)',
  'reports.no_orgs_found': 'No organizations matching search',
  'reports.filter_team': 'Team:',
  'reports.filter_designation': 'Designation:',
  'reports.filter_region': 'Region:',
  'reports.filter_result': 'Result:',
  'reports.all_results': 'All Results',
  'reports.clear_filters': 'Clear',

  // Scenario Buttons & Details
  'scenarios.back_to_scenarios': '← Back to Scenarios',
  'scenarios.assign_learners': 'Assign Learners',
  'scenarios.organization_access': 'Organization Access',
  'scenarios.duplicate': 'Duplicate',
  'scenarios.llm_prompts': 'LLM Evaluation Prompts',
  'scenarios.edit_scenario': 'Edit Scenario',
  'scenarios.learners_assigned': 'Learners Assigned',
  'scenarios.create_scenario': 'Create Scenario',
  'scenarios.category': 'Category:',
  'scenarios.difficulty': 'Difficulty:',
  'scenarios.difficulty_label': 'Difficulty',
  'scenarios.revert_draft': 'Revert to Draft',
  'scenarios.publish_scenario': 'Publish Scenario',
  'scenarios.publish_and_assign': '🚀 Publish & Assign',
  'scenarios.archive': 'Archive',
  'scenarios.start_simulation': 'Start Simulation',
  'scenarios.loading_dossier': 'Loading scenario dossier…',
  'scenarios.not_found': 'Scenario not found',
  'scenarios.download_template': 'Download Template',
  'scenarios.import_json': 'Import JSON',
  'scenarios.back_to_simulations': 'Back to simulations',
  'scenarios.your_role': 'Your Role',
  'briefing.enter_room': 'Enter Simulation Room →',
  'briefing.launching': 'Launching Simulation…',
  'briefing.resuming': 'Resuming Simulation…',
  'briefing.loading': 'Loading scenario briefing dossier…',
  'briefing.error_title': 'Unable to load scenario',
  'briefing.back_to_dashboard': '← Back to Dashboard',
  'briefing.default_role': 'Customer Support Rep',
  'briefing.background_mission_title': 'Scenario Background & Learner Mission',
  'briefing.key_mission_label': 'Key Mission',
  'briefing.default_mission': 'De-escalate the customer, clarify the underlying request, and resolve the inquiry within compliance standards.',
  'briefing.persona_dossier_title': 'Customer Profile Dossier',
  'briefing.personas_dossier_title_plural': 'Scenario Personas Dossier ({{count}} AI Participants)',
  'briefing.persona_number': 'Persona #{{n}}',
  'briefing.persona_role_label': 'Role:',
  'briefing.persona_temperament_label': 'Temperament:',
  'briefing.persona_role_participant': 'Participant',
  'briefing.persona_temperament_challenging': 'Challenging / Urgent',
  'briefing.competency_matrix_title': 'Evaluation Competency Matrix',
  'briefing.rubric_weight': 'Weight',
  'briefing.rubric_empathy': 'Empathy & Active Listening',
  'briefing.rubric_resolution': 'Problem Resolution',
  'briefing.rubric_compliance': 'Compliance & Protocol',
  'briefing.rules_title': 'Rules of Engagement',
  'briefing.rule1_title': 'Natural Speech Required',
  'briefing.rule1_desc': 'Speak clearly as you would on a live voice customer call.',
  'briefing.rule2_title': 'Real-Time Interactive AI',
  'briefing.rule2_desc': 'The AI persona will respond dynamically based on your questions and empathy.',
  'briefing.rule3_title': 'Privacy & Video Telemetry',
  'briefing.rule3_desc': 'Audio/video telemetry is processed securely for AI coaching and scoring.',
  'briefing.language_title': 'Language Required',
  'briefing.step_1': 'Step 1',
  'briefing.select_language_desc': 'Select your preferred conversation language for this simulation:',
  'briefing.select_language_aria': 'Preferred conversation language',
  'briefing.ready_badge': 'Ready',
  'briefing.readiness_title': 'System Readiness',
  'briefing.step_2': 'Step 2',
  'briefing.auto_diagnostics': 'Auto-diagnostics running',
  'briefing.testing_button': 'Testing…',
  'briefing.rerun_all': '↻ Re-run All',
  'briefing.optional_paren': '(Optional)',
  'briefing.compulsory_paren': '(Compulsory)',
  'briefing.mic_subtext': 'Voice input capture',
  'briefing.camera_title': 'Camera & Video',
  'briefing.camera_subtext': 'Visual attention telemetry',
  'briefing.speaker_title': 'Speaker Playback',
  'briefing.speaker_subtext': 'AI customer persona voice',
  'briefing.sound_playing': '🔊 Playing…',
  'briefing.test_sound': '▶ Test Sound',
  'briefing.network_title': 'Network Latency',
  'briefing.network_subtext': 'AI server connection',
  'briefing.status.optional': 'Optional',
  'briefing.status.checking': 'Checking…',
  'briefing.status.ready': '✓ Ready',
  'briefing.status.testing': '⏳ Testing…',
  'briefing.status.optional_passed': '✓ Optional',
  'briefing.status.failed': '⚠️ Failed',
  'briefing.status.pending': 'Pending',
  'briefing.status.ok': '✓ OK',
  'briefing.allowance_reached_title': 'Scenario Attempt Allowance Reached',
  'briefing.all_attempts_completed_title': 'All Attempts Completed',
  'briefing.allowance_reached_desc_with_total': 'This scenario\'s attempt allowance is fully used ({{count}} of {{total}}). Purchase an attempt pack or ask your administrator to add attempts.',
  'briefing.allowance_reached_desc': 'This scenario\'s attempt allowance is fully used. Purchase an attempt pack or ask your administrator to add attempts.',
  'briefing.max_attempts_reached_desc': 'You have reached the maximum allowed attempts ({{used}} of {{max}} completed) for this simulation. No additional attempts can be started.',
  'briefing.allowance_reached_btn': 'Attempt Allowance Reached',
  'briefing.max_attempts_reached_btn': 'Max Attempts Reached ({{used}}/{{max}})',
  'briefing.view_past_scorecards': 'View Past Attempt Scorecards →',
  'briefing.active_attempt_title': 'Active Attempt In Progress',
  'briefing.active_attempt_desc': 'You already have an active attempt{{attempt}} in progress. Duplicate attempts are not allowed. You can resume your session directly.',
  'briefing.active_attempt_number_tag': ' (Attempt #{{n}})',
  'briefing.resume_in_progress': 'Resume In-Progress Attempt →',
  'briefing.consent_text': 'I agree to the assessment recording policy and consent to AI-based conversation analysis and competency evaluation.',
  'briefing.view_terms': 'View Terms & Consent Policy ↗',
  'briefing.running_hardware_checks': 'Running the required hardware checks. The room unlocks as soon as they pass.',
  'briefing.complete_system_checks': 'Complete the required system checks above to unlock the room. Retry any failed check.',
  'briefing.consent_required_btn': 'Consent Required to Enter Room',
  'briefing.checking_hardware_btn': 'Checking Hardware…',
  'briefing.complete_checks_btn': 'Complete System Checks',
  'briefing.start_note': 'Simulation time starts immediately upon entering the room. ({{remaining}} of {{max}} attempts remaining)',
  'briefing.start_simulation_aria': 'Start simulation',
  'briefing.launching_short': 'Launching…',
  'briefing.attempt_in_progress_short': 'Attempt in progress',
  'briefing.dock_step_consent': 'Step 3 · consent required',
  'briefing.dock_step_hardware': 'Step 2 · hardware checks',
  'briefing.dock_ready': 'Ready to launch',
  'briefing.dock_ready_to_resume': 'Session ready to resume',
  'briefing.dock_attempts_left': '{{remaining}} of {{max}} attempts left',
  'briefing.dock_resume_btn': 'Resume Attempt →',
  'briefing.dock_consent_btn': 'Review & consent ↓',
  'briefing.dock_checks_btn': 'Complete checks ↓',
  'briefing.dock_enter_btn': 'Enter Room →',
  'briefing.overlay_resuming_title': 'Resuming your simulation…',
  'briefing.overlay_preparing_title': 'Preparing your simulation room…',
  'briefing.overlay_desc': 'Creating the attempt, locking your voice settings and loading the scenario. This can take a few seconds — keep this tab open.',
  'briefing.overlay_cancel': 'Cancel and stay here',

  // Scenario List Stats (admin-stats-grid)
  'scenarios.stats.total': 'Total Scenarios',
  'scenarios.stats.total_sub': 'Authored simulation cases',
  'scenarios.stats.published': 'Published & Active',
  'scenarios.stats.published_sub': 'Live for learner practice',
  'scenarios.stats.draft': 'In Draft Authoring',
  'scenarios.stats.draft_sub': 'Configuration in progress',

  // Analytics Stats (admin-stats-grid)
  'analytics.stats.total_attempts': 'Total Simulation Attempts',
  'analytics.stats.avg_score': 'Average Proficiency Score',
  'analytics.stats.avg_score_sub': 'Across all scored evaluation attempts',
  'analytics.stats.pass_rate': 'Benchmark Pass Rate',
  'analytics.stats.pass_rate_sub': 'Standard threshold benchmark: 70%',
  'analytics.stats.active_scenarios': 'Active Scenarios',
  'analytics.stats.total_scenarios_sub': 'total designed cases',

  // Audit Log Stats (admin-stats-grid)
  'audit.stats.total_events': 'Total Recorded Events',
  'audit.stats.successful_auth': 'Successful Authorizations',
  'audit.stats.user_mutations': 'User & Role Mutations',
  'audit.stats.scenario_ops': 'Scenario Operations',

  // Tenant Details Stats (admin-stats-grid)
  'tenant.stats.users': 'Users',
  'tenant.stats.scenarios': 'Scenarios',
  'tenant.stats.attempts_run': 'Attempts Run',

  // Video Observations Extended
  'scorecard.face_centering': 'Face Centering',
  'scorecard.sampled_frames': 'Sampled Frames',
  'scorecard.model_confidence': 'Model Confidence',
  'scorecard.missing_intervals': '{{count}} missing intervals',
  'scorecard.framing_alignment': 'Framing zone alignment',
  'scorecard.optimal_positioning': 'Optimal camera positioning',
  'scorecard.framing_omitted': 'Framing telemetry omitted',
  'scorecard.valid_samples': '100% valid samples',
  'scorecard.failed_samples': '{{count}} failed samples',
  'scorecard.detection_certainty': 'Detection certainty',
  'scorecard.no_face_intervals': 'No-Face Intervals ({{count}})',
  'scorecard.no_face_desc': 'Intervals when learner was out of frame',
  'scorecard.interval_num': 'Interval #{{num}}: {{start}} – {{end}}',
  'scorecard.duration_sec': '{{sec}}s duration',
  'scorecard.warning_tag': '(Warning)',
  'scorecard.head_pose_title': 'Head-Pose Orientation Observations',
  'scorecard.supported': 'Supported',
  'scorecard.unsupported': 'Unsupported',
  'scorecard.within_limits': 'Within Limits',
  'scorecard.avg_angles': 'Avg Yaw / Pitch / Roll',
  'scorecard.max_rotation': 'Max Rotation',
  'scorecard.facial_expressions_title': 'Approximate Facial Expressions (face-api.js)',
  'scorecard.on_device_outputs': 'On-Device Model Outputs',
  'scorecard.assessment_integrity_notice': 'Assessment Integrity Notice: Physical camera metrics reflect camera framing and on-device detection confidence. They are provided as observational context only and do not alter conversational competency scores.',
  'scorecard.telemetry_not_recorded': 'Video telemetry was not recorded during this session (microphone audio only). Visual metrics such as framing and face centering are omitted from this scorecard without penalty to your competency score.',
  'scorecard.audio_recorded_evaluated': 'Audio Recorded & Evaluated',
  'scorecard.verbal_competency_intact': 'Verbal Competency Intact',
  'scorecard.retrieving_telemetry': 'Retrieving video evidence telemetry...',
  'scorecard.video_failed_desc': 'Video analysis could not be completed for this attempt.',
  'scorecard.head_pose_unsupported': 'Head-pose estimation is unsupported or landmark angles were unavailable from the detector.',
  'scorecard.facial_notice': 'Notice: Facial expression labels are approximate local model observations. They are separate from dialogue evaluation and do not directly increase or decrease competency scores.',

  // Communication & Delivery Grid
  'scorecard.comm_delivery_title': 'Communication & delivery',
  'scorecard.from_voice_and_transcript': 'From your voice and transcript',
  'scorecard.speaking_pace': 'Speaking pace',
  'scorecard.words_per_min': 'words/min',
  'scorecard.ideal_pace': '130 - 160 Ideal',
  'scorecard.filler_words_title': 'Filler words & pauses',
  'scorecard.total_fillers': 'total · {{fpm}}/min',
  'scorecard.view_evidence': '🔍 View',
  'scorecard.turn_taking_title': 'Turn-taking balance',
  'scorecard.learner_share': 'Learner',
  'scorecard.customer_share': 'Customer',
  'scorecard.listening_ratio': 'Listening ratio',
  'scorecard.clarity_articulation': 'Clarity & articulation',
  'scorecard.response_latency': 'Response latency',
  'scorecard.interruptions_overlaps': 'Speech overlaps',
  'scorecard.emotional_tone': 'Emotional tone',
  'scorecard.energy_engagement': 'Energy & engagement',
  'scorecard.status_good': 'Good',
  'scorecard.status_watch': 'Watch',
  'scorecard.status_needs_work': 'Needs Work',

  // Replay & Key Moments Card
  'scorecard.replay_moments_title': 'Replay & Key Moments',
  'scorecard.replay_moments_subtitle': 'Review pivotal turns from your conversation with interactive playback and coaching tips',
  'scorecard.waveform_mode': 'Waveform',
  'scorecard.video_player_mode': 'Video Player',
  'scorecard.filter_all': 'All Moments ({{count}})',
  'scorecard.filter_improvement': 'Needs Work',
  'scorecard.filter_strength': 'Strength',
  'scorecard.ai_customer': 'AI Customer',
  'scorecard.learner_you': 'You (Learner)',
  'scorecard.play_moment': 'Play Moment',
  'scorecard.pause_moment': 'Pause',
  'scorecard.jump_to_moment': 'Jump to Moment',
  'scorecard.coach_recommendation': 'Coach Recommendation',
  'scorecard.targeted_drill': 'Targeted Micro-Drill',
  'scorecard.practice_drill': 'Practice this turn',
  'scorecard.customer_prompt': 'Customer Prompt',
  'scorecard.what_you_said': 'What you said',
  'scorecard.speed_1x': '1.0x',
  'scorecard.speed_125x': '1.25x',
  'scorecard.speed_15x': '1.5x',
  'scorecard.no_moments_found': 'No key moments recorded for this filter.',

  // Conversation Transcript Card
  'scorecard.transcript_title': 'Conversation transcript',
  'scorecard.transcript_sub': 'Click to load verbatim turns',
  'scorecard.show_transcript': 'Show Transcript',
  'scorecard.hide_transcript': 'Hide Transcript',
  'scorecard.all_turns': 'All Turns ({{count}})',
  'scorecard.learner_turns': 'Learner ({{count}})',
  'scorecard.ai_turns': 'AI Persona ({{count}})',
  'scorecard.open_full_modal': 'Open in Full Modal ↗',
  'scorecard.turn_n': 'Turn #{{n}}',
  'scorecard.empty_turns_filter': 'No conversation turns recorded for this filter.',

  // Transcript Viewer Modal
  'scorecard.modal_transcript_title': 'Dialogue Communication Transcript',
  'scorecard.modal_transcript_sub': 'Verbatim turns recorded during the live simulation',
  'scorecard.copy_transcript_text': '📋 Copy Text',
  'scorecard.transcript_copied': '✓ Copied',
  'scorecard.close_modal': 'Close',
  'scorecard.total_turns_count': 'Total Dialogue Turns: {{count}}',

  // Inspect Prompts Modal
  'scorecard.inspect_prompts_title': 'Inspect LLM Prompts & Evidence',
  'scorecard.inspect_prompts_sub': 'Audit the underlying system prompts, input transcripts, and rubric criteria sent to the LLM evaluator',
  'scorecard.tab_input_data': '📥 Input Data to LLM ({{count}} turns)',
  'scorecard.tab_system_instruction': 'System Instruction',
  'scorecard.tab_evaluated_rubrics': 'Evaluated Rubrics ({{count}})',
  'scorecard.tab_raw_response': 'Raw JSON Output',
  'scorecard.copy_prompts_json': '📋 Copy Prompt JSON',
  'scorecard.prompts_copied': '✓ Copied JSON',
  'scorecard.eval_provider_label': 'Evaluation Provider:',

  // Evaluation Status & Result Error
  'scorecard.scoring_dialogue_title': 'Scoring Simulation Dialogue…',
  'scorecard.eval_processing_title': 'Evaluation Processing',
  'scorecard.eval_processing_desc': 'The AI evaluation engine is analyzing your conversation transcript against competency rubrics and evidence benchmarks.',
  'scorecard.evaluating_in_progress': 'Evaluating Transcript...',
  'scorecard.check_status_btn': 'Check Evaluation Status',
  'scorecard.error_results_unavailable': 'Evaluation Results Unavailable',
  'scorecard.error_default_msg': 'Could not retrieve evaluation results for this attempt. Please check your network connection and try again.',
  'scorecard.my_attempts_nav': 'My Attempts',
  'scorecard.scoring_evaluating_msg': 'Scoring and evaluating your attempt performance...',
  'scorecard.results_generating_msg': 'Attempt evaluation results are generating...',
};

